import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  List,
  ListItemButton,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FolderIcon from '@mui/icons-material/Folder';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import RefreshIcon from '@mui/icons-material/Refresh';
import HomeIcon from '@mui/icons-material/Home';
import CheckIcon from '@mui/icons-material/Check';
import { API_GET, API_POST, DBRequest, useConfig } from '../../../api/api';
import { tokens } from '../../../global/theme/tokens';

/**
 * Browses the folder structure of an existing network storage so the user can
 * pick a folder. Talks to the `network-storages/{id}/browse` endpoint and can
 * create new folders via `network-storages/{id}/folders`.
 */
const NetworkStorageExplorer = ({ storageId, value = '', onChange }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const config = useConfig();

  const [path, setPath] = useState('');
  const [entry, setEntry] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [showNewFolder, setShowNewFolder] = useState(false);

  // The path we last loaded from the server. Lets us tell an external value
  // change (prefill when editing) apart from our own automatic selection.
  const loadedPathRef = useRef(null);

  function loadPath(targetPath) {
    if (!config || storageId === undefined || storageId === null) return;
    setError(null);
    DBRequest({
      config,
      path: `network-storages/${storageId}/browse?path=${encodeURIComponent(
        targetPath,
      )}`,
      method: API_GET,
      onLoading: setIsLoading,
      onResponse: (res) => {
        const resolvedPath = res.data.path ?? targetPath;
        setEntry(res.data);
        setPath(resolvedPath);
        loadedPathRef.current = resolvedPath;
        // The folder currently shown is the selection - no confirm step.
        if (value !== resolvedPath) onChange?.(resolvedPath);
      },
      onError: (err) => setError(err?.message || t('Could not browse storage')),
    });
  }

  // Open at the current value so editing prefills the saved folder, and follow
  // any external value change that we did not trigger ourselves.
  useEffect(() => {
    if (!config || storageId === undefined || storageId === null) return;
    if (value === loadedPathRef.current) return;
    loadPath(value || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, storageId, value]);

  function handleCreateFolder() {
    const name = newFolderName.trim();
    if (!name) return;
    DBRequest({
      config,
      path: `network-storages/${storageId}/folders`,
      method: API_POST({ path, name }),
      onLoading: setIsLoading,
      onResponse: () => {
        setNewFolderName('');
        setShowNewFolder(false);
        loadPath(path);
      },
      onError: (err) =>
        setError(err?.message || t('Could not create folder')),
    });
  }

  const segments = path ? path.split('/') : [];

  function navigateToSegment(index) {
    loadPath(segments.slice(0, index + 1).join('/'));
  }

  return (
    <Box
      className="flex flex-col rounded-lg overflow-hidden w-full"
      sx={{ bgcolor: colors.card, border: `1px solid ${colors.glass}` }}
    >
      {/* Breadcrumb */}
      <Box
        className="flex flex-row items-center gap-1 px-2 py-1 flex-wrap"
        sx={{ bgcolor: colors.glassSolid }}
      >
        <IconButton size="small" onClick={() => loadPath('')}>
          <HomeIcon fontSize="small" />
        </IconButton>
        {segments.map((segment, index) => (
          <Box key={index} className="flex flex-row items-center gap-1">
            <Typography variant="caption">/</Typography>
            <Button
              size="small"
              onClick={() => navigateToSegment(index)}
              sx={{ minWidth: 'unset', textTransform: 'none' }}
            >
              {segment}
            </Button>
          </Box>
        ))}
        <Box className="flex-1" />
        <IconButton size="small" onClick={() => loadPath(path)}>
          <RefreshIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => setShowNewFolder((prev) => !prev)}
        >
          <CreateNewFolderIcon fontSize="small" />
        </IconButton>
      </Box>

      {showNewFolder && (
        <Box className="flex flex-row gap-2 p-2 items-center">
          <TextField
            size="small"
            fullWidth
            label={t('New Folder')}
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
          />
          <Button variant="contained" onClick={handleCreateFolder}>
            {t('Create')}
          </Button>
        </Box>
      )}

      {/* Folder list */}
      <Box className="relative" sx={{ minHeight: '160px', maxHeight: '320px' }}>
        {isLoading && (
          <Box
            className="absolute inset-0 flex items-center justify-center z-10"
            sx={{ bgcolor: `${colors.card}cc` }}
          >
            <CircularProgress size="2rem" />
          </Box>
        )}
        {error ? (
          <Typography className="p-4" color="error">
            {error}
          </Typography>
        ) : (
          <List dense className="overflow-y-auto" sx={{ maxHeight: '320px' }}>
            {entry && entry.directories.length === 0 && (
              <Typography className="p-4 opacity-60" variant="body2">
                {t('No folders')}
              </Typography>
            )}
            {entry &&
              entry.directories.map((dir) => (
                <ListItemButton
                  key={dir.path}
                  onClick={() => loadPath(dir.path)}
                  className="flex flex-row gap-2"
                >
                  <FolderIcon fontSize="small" sx={{ color: colors.selected }} />
                  <Typography variant="body2">{dir.name}</Typography>
                </ListItemButton>
              ))}
          </List>
        )}
      </Box>

      {/* Selection footer - shows the folder that is currently selected */}
      <Box
        className="flex flex-row items-center gap-2 px-2 py-1"
        sx={{ bgcolor: colors.glassSolid }}
      >
        <CheckIcon fontSize="small" sx={{ color: colors.success }} />
        <Typography variant="caption" className="monospace truncate">
          {path ? `/${path}` : '/'}
        </Typography>
      </Box>
    </Box>
  );
};

export default NetworkStorageExplorer;
